import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import UserGroup from "../../../assets/images/user-group";
import ChildrenGroup from "../../../assets/images/children-group";
import Add from "@material-ui/icons/Add";
import { selectionStore } from "../../../stores/selection.store";
import { moduleSettings } from "../../../stores/module-settings.store";
import { getTagManagerService } from "../../../services/tag-manager.service";
import selectionStoreService from "../../../services/selection-guests-service";
import selectionGuestsService from "../../../services/selection-guests-service";
import { SlotItem } from "../slot-item/slot-item";
import { DropdownOption } from "../../../types";
import { InputWithIcon } from "../../input-with-icon/input-with-icon";
import { selectionStoreChangesService } from "../../../services/selection-store-changes.service";
import { GolEventListener } from "../../calendar/calendar";
import ReactPixel from "react-facebook-pixel";
import TextField from "@material-ui/core/TextField";
import { BackgroundCircleButton } from "../../background-circle-button/background-circle-button";

interface IStyle {
  isChildren: boolean;
}
export interface ISelectionGuest {
  isFirstStep?: boolean;
  type: string;
}

const SelectionGuestsWrapper = styled.div<IStyle>`
  margin: 0 6px 4px;
  margin-top: ${(props) => (props.isChildren ? "6px" : "")};
`;

const GuestSlots = styled.div``;

const GuestInput = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  width: 100%;
  padding: 6px;

  svg {
    cursor: pointer;
  }

  #guest-input {
    width: 36px;
    padding: 6px 12px;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }

  svg {
    margin-left: 8px;
  }

  svg#check-icon {
    fill: ${(props) => props.theme.primaryColor} !important;
  }
`;

const GuestSlotsWrapper = styled.div`
  width: 300px;
  background-color: ${(props) => props.theme.backgroundColor};
  padding: ${(props) => props.theme.globalPadding} 6px;
  margin-bottom: 4px;
  align-items: center;
  box-shadow: 0 2px 6px rgb(0 0 0 / 5%), 0 0 0 1px rgb(0 0 0 / 7%);
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
`;

const TextButton = styled.a`
  font-size: 12px;
  margin: 8px 0;
  cursor: pointer;
`;

const Guests = (props: ISelectionGuest) => {
  const { t } = useTranslation();
  const isFirstStep =
    props.isFirstStep === undefined ? true : props.isFirstStep;
  const isChildren = props.type == "children";
  const [options] = useState(selectionStoreService.guestsDropdownOption());
  const [showInput, setShowInput] = useState(false);
  const [restaurantId] = useState(moduleSettings.getRestaurantId());
  const [isSlotsDisplayed, setIsSlotsDisplayed] = useState(false);
  const [inputGuests, setInputGuests] = useState(0);
  const [selectedGuests, setSelectedGuests] = useState(
    isChildren ? selectionStore.children : selectionStore.guests
  );

  getTagManagerService(restaurantId).pushTags("pax", {
    first_step: isFirstStep,
  });
  ReactPixel.pageView();

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "selection-guests",
      [GolEventListener.Guests],
      () => {
        isChildren
          ? setSelectedGuests(selectionStore.children)
          : setSelectedGuests(selectionStore.guests);
      }
    );
    return () => {
      selectionStoreChangesService.unregisterListener("selection-guests");
    };
  }, []);

  useEffect(() => {
    if (inputGuests > 0) {
      const debounceTimer = setTimeout(() => {
        storeSelectedGuests(inputGuests);
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [inputGuests]);

  const storeSelectedGuests = (value: any): void => {
    setSelectedGuests(Number(value));
    if (isChildren) {
      selectionStore.children = Number(value);
      selectionGuestsService.setDefaultSelectedGuestWhenChildren();
    } else {
      selectionStore.guests = Number(value);
    }
    selectionStoreChangesService.dispatch(GolEventListener.Guests);
  };

  const selectedGuestsClicked = (): void => {
    setIsSlotsDisplayed(!isSlotsDisplayed);
    setShowInput(false);
  };

  const renderOption = (option: DropdownOption) => {
    return (
      <SlotItem
        key={option.value}
        value={option.value}
        isSelected={Number(option.value) === selectedGuests}
        onClick={handleClickSlot}
      />
    );
  };

  const handleClickSlot = (value: any): void => {
    storeSelectedGuests(value);
    setIsSlotsDisplayed(false);
  };

  const handleClick = () => {
    if (showInput && inputGuests > 0) {
      storeSelectedGuests(inputGuests);
      setIsSlotsDisplayed(false);
    } else {
      setInputGuests(0);
      storeSelectedGuests(0);
    }
    setShowInput((showInput) => !showInput);
  };

  const buttonShowHideInput = showInput ? (
    <BackgroundCircleButton
      onClick={handleClick}
      disabled={!(inputGuests > 0)}
    />
  ) : (
    <Add onClick={handleClick} data-test="button-show-input" />
  );

  const handleResetChildren = () => {
    setSelectedGuests(0);
    selectionStore.children = 0;
    selectionStoreChangesService.dispatch(GolEventListener.Guests);
    setIsSlotsDisplayed(!isSlotsDisplayed);
  };

  const changeInputGuests = (e: any) => {
    setInputGuests(Number(e.target.value));
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleClick();
    }
  };

  const resetChildrenButton = (
    <TextButton onClick={handleResetChildren} data-test="reset-button">
      {t("button.none")}
    </TextButton>
  );

  return (
    <SelectionGuestsWrapper
      data-test={`selection-${props.type}`}
      isChildren={isChildren}
    >
      <InputWithIcon
        error={false}
        value={selectionGuestsService.setSelectedGuestsToString(
          selectedGuests,
          props.type
        )}
        placeholderKey={selectionGuestsService.setTypeOfGuestToPlaceholder(
          props.type
        )}
        helperText={""}
        variant={"outlined"}
        onChange={storeSelectedGuests}
        onClick={selectedGuestsClicked}
        icon={isChildren ? <ChildrenGroup /> : <UserGroup />}
      />
      <>
        {isSlotsDisplayed && (
          <GuestSlotsWrapper data-test="guest-slots-wrapper">
            {isChildren && selectedGuests > 0 && resetChildrenButton}
            <GuestSlots data-test="guest-slots">
              {options
                .slice(0, 8)
                .map((option: DropdownOption) => renderOption(option))}
            </GuestSlots>
            <GuestInput>
              {showInput && (
                <TextField
                  autoFocus
                  type="number"
                  id="guest-input"
                  data-test="guest-input"
                  variant="outlined"
                  onChange={changeInputGuests}
                  onKeyDown={handleKeyDown}
                />
              )}
              {buttonShowHideInput}
            </GuestInput>
          </GuestSlotsWrapper>
        )}
      </>
    </SelectionGuestsWrapper>
  );
};
export const SelectionGuests = memo(Guests);
